import axios from 'axios';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { PLATFORM_EVENT_TYPES } from '../constants/platformEvents.generated';

export type PlatformEventType =
  typeof PLATFORM_EVENT_TYPES[keyof typeof PLATFORM_EVENT_TYPES];

export interface EventAttribution {
  sectionId?: number | string | null;
  lessonId?: number | string | null;
}

interface PendingEvent {
  event_type: PlatformEventType;
  occurred_at: string;
  client_timezone: string;
  section_id: number | null;
  lesson_id: number | null;
  context: Record<string, unknown>;
}

const ENDPOINT = '/api/events/';
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 20;
const MAX_BATCH = 50;

let queue: PendingEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

let lastReportAt = 0;
const REPORT_THROTTLE_MS = 60000;
const reportTrackerFailure = (
  error: unknown,
  context: Record<string, unknown> = {},
): void => {
  try {
    const now = Date.now();
    if (now - lastReportAt < REPORT_THROTTLE_MS) {
      return;
    }
    lastReportAt = now;
    Sentry.withScope((scope) => {
      scope.setTag('subsystem', 'platform_events');
      scope.setLevel('warning');
      Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    });
  } catch {
    // reporting must itself never throw
  }
};

const toId = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clientTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

const send = (events: PendingEvent[], useKeepalive: boolean): void => {
  try {
    if (!events.length) {
      return;
    }
    if (useKeepalive && typeof fetch === 'function') {
      // Page is unloading: axios may be cancelled; keepalive fetch survives.
      try {
        const authHeader = axios.defaults.headers.common?.Authorization as
          | string
          | undefined;
        const base = String(axios.defaults.baseURL || '').replace(/\/$/, '');
        const url = base + ENDPOINT;
        fetch(url, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(authHeader ? { Authorization: authHeader } : {}),
          },
          body: JSON.stringify({ events }),
        })
          .then((res) => {
            if (!res.ok) {
              reportTrackerFailure(
                new Error(`platform events keepalive HTTP ${res.status}`),
                {
                  transport: 'keepalive',
                  status: res.status,
                },
              );
            }
          })
          .catch((error) => reportTrackerFailure(error, { transport: 'keepalive' }));
      } catch (error) {
        // Fire-and-forget: analytics failures (including circular context) must
        // never throw inside the visibilitychange handler.
        reportTrackerFailure(error, { transport: 'keepalive' });
      }
      return;
    }
    axios.post(ENDPOINT, { events }).catch((error) => {
      // Fire-and-forget: analytics failures must never surface to the user.
      reportTrackerFailure(error, { transport: 'axios', count: events.length });
    });
  } catch (error) {
    reportTrackerFailure(error, { transport: useKeepalive ? 'keepalive' : 'axios' });
  }
};

export const flushPlatformEvents = (useKeepalive = false): void => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  while (queue.length) {
    send(queue.splice(0, MAX_BATCH), useKeepalive);
  }
};

export const trackPlatformEvent = (
  type: PlatformEventType,
  attribution: EventAttribution = {},
  context: Record<string, unknown> = {},
): void => {
  try {
    const normalizedContext: Record<string, unknown> = {};
    Object.entries(context).forEach(([key, value]) => {
      normalizedContext[key] =
        key.endsWith('_id') && (typeof value === 'string' || typeof value === 'number')
          ? toId(value)
          : value;
    });

    queue.push({
      event_type: type,
      occurred_at: new Date().toISOString(),
      client_timezone: clientTimezone(),
      section_id: toId(attribution?.sectionId),
      lesson_id: toId(attribution?.lessonId),
      context: normalizedContext,
    });
    if (queue.length >= MAX_QUEUE) {
      flushPlatformEvents();
      return;
    }
    if (!flushTimer) {
      flushTimer = setTimeout(() => {
        flushTimer = null;
        flushPlatformEvents();
      }, FLUSH_INTERVAL_MS);
    }
  } catch (error) {
    reportTrackerFailure(error, { transport: 'track', event_type: type });
  }
};

export const usePlatformEventOnMount = (
  type: PlatformEventType,
  attribution: EventAttribution = {},
  context: Record<string, unknown> = {},
): void => {
  useEffect(() => {
    trackPlatformEvent(type, attribution, context);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushPlatformEvents(true);
    }
  });
}

/** Test-only: clear module state between jest cases. */
// eslint-disable-next-line no-underscore-dangle
export const __resetForTests = (): void => {
  queue = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  lastReportAt = 0;
};
